/**
 * Lint library + mute filter (PR Q2 · §Q2.2).
 *
 * Wraps `runLint` + `LINT_RULES` into a UI-friendly shape (rule
 * description, current count, sparkline history) and provides the
 * `filterMuted` predicate the LintDashboardView uses to suppress
 * snoozed findings.
 *
 * History is a stub today (single-tick sparkline). The component shows
 * "no history" copy when length is 1 — v2 will wire real snapshots.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { runLint, LINT_RULES, type LintFinding, type LintSeverity } from '@atlas-manifest/lint';

export interface RuleLibraryRow {
  id: string;
  description: string;
  severity: LintSeverity;
  /** Findings against this rule in the current manifest. */
  currentCount: number;
  /** Per-snapshot history (stub today: [currentCount]). */
  history: number[];
}

export function ruleLibrary(manifest: AtlasManifest): RuleLibraryRow[] {
  const report = runLint(manifest);
  const byRule = new Map<string, number>();
  for (const f of report.findings) {
    byRule.set(f.ruleId, (byRule.get(f.ruleId) ?? 0) + 1);
  }
  const rows = LINT_RULES.map((r) => ({
    id: r.id,
    description: r.description,
    severity: r.severity,
    currentCount: byRule.get(r.id) ?? 0,
    history: [byRule.get(r.id) ?? 0],
  }));
  // Non-zero rules first (so the sidebar leads with the noisy ones),
  // then alphabetical within each group.
  return rows.sort((a, b) => {
    if (a.currentCount > 0 && b.currentCount === 0) return -1;
    if (a.currentCount === 0 && b.currentCount > 0) return 1;
    return a.id.localeCompare(b.id);
  });
}

export interface Mute {
  mutedUntil: number;
  mutedBy: string;
  reason: string;
}

export function muteKey(ruleId: string, ref: string): string {
  return `${ruleId}:${ref}`;
}

export function filterMuted(
  findings: readonly LintFinding[],
  mutes: Readonly<Record<string, Mute>>,
  now: number,
): LintFinding[] {
  return findings.filter((f) => {
    const m = mutes[muteKey(f.ruleId, f.ref)];
    if (!m) return true;
    return m.mutedUntil <= now; // expired ⇒ keep
  });
}
